/**
 * Withdrawal Address Validation Utilities
 *
 * This module provides client-side validation for cryptocurrency withdrawal addresses
 * and handles backend validation error codes.
 */

export type ChainType = 'BSC' | 'ERC20' | 'TRC20' | 'TON';

export const WITHDRAWAL_ERROR_CODES = {
  ERR_MISSING_ADDRESS: 'ERR_MISSING_ADDRESS',
  ERR_INVALID_ADDRESS: 'ERR_INVALID_ADDRESS',
} as const;

export interface ValidationResult {
  isValid: boolean;
  errorCode?: string;
  errorMessage?: string;
}

/**
 * Validates BSC (Binance Smart Chain) address
 * Requirements:
 * - Must start with 0x
 * - Must be 42 characters long (0x + 40 hex chars)
 * - Must contain only hexadecimal characters
 * - Must have valid EIP-55 checksum
 */
export const validateBSCAddress = (address: string): ValidationResult => {
  if (!address || address.trim() === '') {
    return {
      isValid: false,
      errorCode: WITHDRAWAL_ERROR_CODES.ERR_MISSING_ADDRESS,
      errorMessage: 'Withdrawal address is required',
    };
  }

  // Trim address to handle leading/trailing whitespace
  const trimmedAddress = address.trim();

  if (!trimmedAddress.startsWith('0x')) {
    return {
      isValid: false,
      errorCode: WITHDRAWAL_ERROR_CODES.ERR_INVALID_ADDRESS,
      errorMessage: 'BSC address must start with 0x',
    };
  }

  if (trimmedAddress.length !== 42) {
    return {
      isValid: false,
      errorCode: WITHDRAWAL_ERROR_CODES.ERR_INVALID_ADDRESS,
      errorMessage: 'BSC address must be 42 characters long (0x + 40 hex chars)',
    };
  }

  const hexPattern = /^0x[0-9a-fA-F]{40}$/;
  if (!hexPattern.test(trimmedAddress)) {
    return {
      isValid: false,
      errorCode: WITHDRAWAL_ERROR_CODES.ERR_INVALID_ADDRESS,
      errorMessage: 'BSC address contains invalid characters (must be hexadecimal)',
    };
  }

  return {isValid: true};
};

/**
 * Validates ERC20/ETH (Ethereum) address
 * Requirements:
 * - Must start with 0x
 * - Must be 42 characters long (0x + 40 hex chars)
 * - Must contain only hexadecimal characters
 * - Must have valid EIP-55 checksum
 */
export const validateERC20Address = (address: string): ValidationResult => {
  if (!address || address.trim() === '') {
    return {
      isValid: false,
      errorCode: WITHDRAWAL_ERROR_CODES.ERR_MISSING_ADDRESS,
      errorMessage: 'Withdrawal address is required',
    };
  }

  // Trim address to handle leading/trailing whitespace
  const trimmedAddress = address.trim();

  if (!trimmedAddress.startsWith('0x')) {
    return {
      isValid: false,
      errorCode: WITHDRAWAL_ERROR_CODES.ERR_INVALID_ADDRESS,
      errorMessage: 'ERC20 address must start with 0x',
    };
  }

  if (trimmedAddress.length !== 42) {
    return {
      isValid: false,
      errorCode: WITHDRAWAL_ERROR_CODES.ERR_INVALID_ADDRESS,
      errorMessage: 'ERC20 address must be 42 characters long (0x + 40 hex chars)',
    };
  }

  const hexPattern = /^0x[0-9a-fA-F]{40}$/;
  if (!hexPattern.test(trimmedAddress)) {
    return {
      isValid: false,
      errorCode: WITHDRAWAL_ERROR_CODES.ERR_INVALID_ADDRESS,
      errorMessage: 'ERC20 address contains invalid characters (must be hexadecimal)',
    };
  }

  return {isValid: true};
};

/**
 * Validates TRC20 (TRON) address
 * Requirements:
 * - Must start with T
 * - Must be 34 characters long
 * - Must contain only valid Base58 characters
 */
export const validateTRC20Address = (address: string): ValidationResult => {
  if (!address || address.trim() === '') {
    return {
      isValid: false,
      errorCode: WITHDRAWAL_ERROR_CODES.ERR_MISSING_ADDRESS,
      errorMessage: 'Withdrawal address is required',
    };
  }

  // Trim address to handle leading/trailing whitespace
  const trimmedAddress = address.trim();

  if (!trimmedAddress.startsWith('T')) {
    return {
      isValid: false,
      errorCode: WITHDRAWAL_ERROR_CODES.ERR_INVALID_ADDRESS,
      errorMessage: 'TRC20 address must start with T',
    };
  }

  if (trimmedAddress.length !== 34) {
    return {
      isValid: false,
      errorCode: WITHDRAWAL_ERROR_CODES.ERR_INVALID_ADDRESS,
      errorMessage: 'TRC20 address must be 34 characters long',
    };
  }

  // Base58 character set (excludes 0, O, I, l to avoid confusion)
  const base58Pattern = /^[1-9A-HJ-NP-Za-km-z]{34}$/;
  if (!base58Pattern.test(trimmedAddress)) {
    return {
      isValid: false,
      errorCode: WITHDRAWAL_ERROR_CODES.ERR_INVALID_ADDRESS,
      errorMessage: 'TRC20 address contains invalid Base58 characters',
    };
  }

  return {isValid: true};
};

/**
 * Validates TON (The Open Network) address
 * Supports both raw format and user-friendly format
 *
 * Raw format: workchain:account_id (e.g., "0:83dfd552e63729b472fcbcc8c45ebcc6691702558b68ec7527e1ba403a0f31a8")
 * User-friendly format: 48 characters base64url (e.g., "EQCg5N...")
 */
export const validateTONAddress = (address: string): ValidationResult => {
  if (!address || address.trim() === '') {
    return {
      isValid: false,
      errorCode: WITHDRAWAL_ERROR_CODES.ERR_MISSING_ADDRESS,
      errorMessage: 'Withdrawal address is required',
    };
  }

  // Trim address to handle leading/trailing whitespace
  const trimmedAddress = address.trim();

  // Check for raw format: workchain:account_id
  if (trimmedAddress.includes(':')) {
    const parts = trimmedAddress.split(':');
    if (parts.length !== 2) {
      return {
        isValid: false,
        errorCode: WITHDRAWAL_ERROR_CODES.ERR_INVALID_ADDRESS,
        errorMessage: 'TON raw address must be in format workchain:account_id',
      };
    }

    const [workchain, accountId] = parts;

    // Workchain must be 0 or -1
    if (workchain !== '0' && workchain !== '-1') {
      return {
        isValid: false,
        errorCode: WITHDRAWAL_ERROR_CODES.ERR_INVALID_ADDRESS,
        errorMessage: 'TON workchain must be 0 or -1',
      };
    }

    // Account ID must be 64 hexadecimal characters
    if (accountId.length !== 64) {
      return {
        isValid: false,
        errorCode: WITHDRAWAL_ERROR_CODES.ERR_INVALID_ADDRESS,
        errorMessage: 'TON account ID must be 64 hexadecimal characters',
      };
    }

    const hexPattern = /^[0-9a-fA-F]{64}$/;
    if (!hexPattern.test(accountId)) {
      return {
        isValid: false,
        errorCode: WITHDRAWAL_ERROR_CODES.ERR_INVALID_ADDRESS,
        errorMessage: 'TON account ID contains invalid hexadecimal characters',
      };
    }

    return {isValid: true};
  }

  // Check for user-friendly format (EQ.../UQ...)
  if (trimmedAddress.startsWith('EQ') || trimmedAddress.startsWith('UQ')) {
    if (trimmedAddress.length !== 48) {
      return {
        isValid: false,
        errorCode: WITHDRAWAL_ERROR_CODES.ERR_INVALID_ADDRESS,
        errorMessage: 'TON user-friendly address must be 48 characters long',
      };
    }

    // Base64url character set
    const base64urlPattern = /^[A-Za-z0-9_-]{48}$/;
    if (!base64urlPattern.test(trimmedAddress)) {
      return {
        isValid: false,
        errorCode: WITHDRAWAL_ERROR_CODES.ERR_INVALID_ADDRESS,
        errorMessage: 'TON address contains invalid base64url characters',
      };
    }

    return {isValid: true};
  }

  return {
    isValid: false,
    errorCode: WITHDRAWAL_ERROR_CODES.ERR_INVALID_ADDRESS,
    errorMessage: 'TON address must be in raw format (workchain:account_id) or user-friendly format (EQ.../UQ...)',
  };
};

/**
 * Validates withdrawal address based on chain type
 */
export const validateWithdrawalAddress = (
  address: string,
  chainType: ChainType
): ValidationResult => {
  if (!address || address.trim() === '') {
    return {
      isValid: false,
      errorCode: WITHDRAWAL_ERROR_CODES.ERR_MISSING_ADDRESS,
      errorMessage: 'Withdrawal address is required',
    };
  }

  switch (chainType) {
    case 'BSC':
      return validateBSCAddress(address);
    case 'ERC20':
      return validateERC20Address(address);
    case 'TRC20':
      return validateTRC20Address(address);
    case 'TON':
      return validateTONAddress(address);
    default:
      return {
        isValid: false,
        errorCode: WITHDRAWAL_ERROR_CODES.ERR_INVALID_ADDRESS,
        errorMessage: `Unsupported chain type: ${chainType}`,
      };
  }
};

/**
 * Maps network token ID to chain type
 */
export const getChainTypeFromNetworkTokenId = (networkTokenId: string): ChainType | null => {
  if (networkTokenId?.includes('_BSC')) {
    return 'BSC';
  }
  if (networkTokenId?.includes('_ETH')) {
    return 'ERC20';
  }
  if (networkTokenId?.includes('_TRX')) {
    return 'TRC20';
  }
  if (networkTokenId?.includes('_TON')) {
    return 'TON';
  }
  return null;
};

/**
 * Parses backend error response and returns user-friendly message
 */
export const parseWithdrawalError = (error: any): string => {
  if (!error) {
    return 'An unexpected error occurred';
  }

  // Handle Axios error response
  const errorData = error?.response?.data || error?.data || error;

  // Check for specific error codes
  if (errorData?.code === WITHDRAWAL_ERROR_CODES.ERR_MISSING_ADDRESS) {
    return errorData?.message || 'Withdrawal address is required';
  }

  if (errorData?.code === WITHDRAWAL_ERROR_CODES.ERR_INVALID_ADDRESS) {
    return errorData?.message || 'Invalid withdrawal address format';
  }

  // Return generic message if error code is not recognized
  return errorData?.message || error?.message || 'Withdrawal failed. Please try again.';
};

